import { useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { EmissionTier, MetricState, canonicalPeriodLabels, CanonicalPeriod } from '../../common'
import { usePropertyClimateData } from '../../../../hooks/climateData'
import { FloodScoreBadge } from '../../common/flood/utils'
import ClimateMetricCard from '../../common/ClimateMetricCard'
import ClimateSectionTitle from '../../common/ClimateSectionTitle'
import {
  BI_DAYS_LABEL,
  buildAnnualLossDisplay,
  DAMAGE_AAL_LABEL,
  FINANCIAL_BREAKDOWN_TITLE,
  formatCurrencyValue,
  formatDamageTargetLabel,
  formatFloodSourceLabel,
  formatPlainNumber,
  parseMetricPointY,
  REVENUE_AAL_LABEL,
  sumMetricNode,
} from '../../common/financialMetrics'
import { useCurrentUser } from '../../../../hooks/users'

const SCORE_METRIC_KEY = 'fld:score'
const DAMAGE_AAL_METRIC_KEY = 'fld:average_damage_loss'
const REVENUE_AAL_METRIC_KEY = 'fld:average_bi_loss'
const BI_DAYS_METRIC_KEY = 'fld:average_bi_days'

const PERIOD_ORDER: CanonicalPeriod[] = [
  CanonicalPeriod.SHORT_TERM,
  CanonicalPeriod.MID_TERM,
  CanonicalPeriod.LONG_TERM,
]

function sortPeriods(periods: string[]): string[] {
  return [...periods].sort((left, right) => PERIOD_ORDER.indexOf(left as CanonicalPeriod) - PERIOD_ORDER.indexOf(right as CanonicalPeriod))
}

function getSucceededMetricData<T>(metric: { state?: { value?: MetricState }; data?: T | null } | undefined) {
  if (!metric || metric.state?.value !== MetricState.SUCCEEDED) return null
  return metric.data ?? null
}

function scaledValue(value: number | null, adaptationFactor: number, applyScaling = true) {
  if (value == null) return null
  return applyScaling ? value * adaptationFactor : value
}

export default function FloodSummary({
  propertyId,
  emissionTier,
  adaptationFactor = 1,
}: {
  propertyId: number
  emissionTier: EmissionTier
  adaptationFactor?: number
}) {
  const {
    data: scoreData,
    isLoading: scoreLoading,
    error: scoreError,
  } = usePropertyClimateData(SCORE_METRIC_KEY, { propertyId })
  const {
    data: damageAalData,
    isLoading: damageAalLoading,
    error: damageAalError,
  } = usePropertyClimateData(DAMAGE_AAL_METRIC_KEY, { propertyId })
  const {
    data: revenueAalData,
    isLoading: revenueAalLoading,
    error: revenueAalError,
  } = usePropertyClimateData(REVENUE_AAL_METRIC_KEY, { propertyId })
  const {
    data: biDaysData,
    isLoading: biDaysLoading,
    error: biDaysError,
  } = usePropertyClimateData(BI_DAYS_METRIC_KEY, { propertyId })

  const { currencySymbol } = useCurrentUser()

  const scorePayload = getSucceededMetricData(scoreData)
  const damageAalPayload = getSucceededMetricData(damageAalData)
  const revenueAalPayload = getSucceededMetricData(revenueAalData)
  const biDaysPayload = getSucceededMetricData(biDaysData)

  const [selectedPeriod, setSelectedPeriod] = useState<CanonicalPeriod>(CanonicalPeriod.MID_TERM)

  const availablePeriods = useMemo(() => {
    const keys = Array.from(
      new Set([
        ...(((scorePayload as any)?.keys?.period_canonical as string[] | undefined) ?? []),
        ...(((damageAalPayload as any)?.keys?.period_canonical as string[] | undefined) ?? []),
        ...(((revenueAalPayload as any)?.keys?.period_canonical as string[] | undefined) ?? []),
        ...(((biDaysPayload as any)?.keys?.period_canonical as string[] | undefined) ?? []),
      ]),
    )

    if (!keys.length) return PERIOD_ORDER as string[]
    return sortPeriods(keys)
  }, [biDaysPayload, damageAalPayload, revenueAalPayload, scorePayload])

  const effectivePeriod = useMemo(() => {
    if (availablePeriods.includes(selectedPeriod)) return selectedPeriod
    if (availablePeriods.includes(CanonicalPeriod.MID_TERM)) return CanonicalPeriod.MID_TERM
    return availablePeriods[0] as CanonicalPeriod
  }, [availablePeriods, selectedPeriod])

  const scoreValue = parseMetricPointY((scorePayload as any)?.data?.[emissionTier]?.[effectivePeriod])
  const adjustedScoreValue =
    scoreValue != null && !Number.isNaN(scoreValue) ? Math.round(scoreValue * adaptationFactor) : null

  const damageAalPeriodNode = (damageAalPayload as any)?.data?.[emissionTier]?.[effectivePeriod]
  const revenueAalPeriodNode = (revenueAalPayload as any)?.data?.[emissionTier]?.[effectivePeriod]
  const biDaysPeriodNode = (biDaysPayload as any)?.data?.[emissionTier]?.[effectivePeriod]

  const damageAalTotal = scaledValue(sumMetricNode(damageAalPeriodNode), adaptationFactor)
  const revenueAalTotal = scaledValue(sumMetricNode(revenueAalPeriodNode), adaptationFactor)
  const biDaysTotal = scaledValue(sumMetricNode(biDaysPeriodNode), adaptationFactor)
  const totalLossDisplay = buildAnnualLossDisplay(damageAalTotal, revenueAalTotal)

  const damageTargets = useMemo(() => {
    const explicit = (((damageAalPayload as any)?.keys?.target as string[] | undefined) ?? []).filter(Boolean)
    if (explicit.length) return explicit
    if (damageAalPeriodNode && typeof damageAalPeriodNode === 'object') return Object.keys(damageAalPeriodNode)
    return []
  }, [damageAalPayload, damageAalPeriodNode])

  const damageSources = useMemo(() => {
    const explicit = (((damageAalPayload as any)?.keys?.source as string[] | undefined) ?? []).filter(Boolean)
    if (explicit.length) return explicit
    const firstTarget = damageTargets[0]
    if (firstTarget && damageAalPeriodNode?.[firstTarget]) {
      return Object.keys(damageAalPeriodNode[firstTarget])
    }
    return []
  }, [damageAalPayload, damageAalPeriodNode, damageTargets])

  const revenueSources = useMemo(() => {
    const explicit = (((revenueAalPayload as any)?.keys?.source as string[] | undefined) ?? []).filter(Boolean)
    if (explicit.length) return explicit
    if (revenueAalPeriodNode && typeof revenueAalPeriodNode === 'object') return Object.keys(revenueAalPeriodNode)
    return []
  }, [revenueAalPayload, revenueAalPeriodNode])

  const damageBreakdownHasData = damageTargets.some(target =>
    damageSources.some(source => scaledValue(sumMetricNode(damageAalPeriodNode?.[target]?.[source]), adaptationFactor) != null),
  )
  const revenueBreakdownHasData = revenueSources.some(
    source => scaledValue(sumMetricNode(revenueAalPeriodNode?.[source]), adaptationFactor) != null,
  )

  const annualLoading = scoreLoading || damageAalLoading || revenueAalLoading || biDaysLoading
  const annualErrored = !!(scoreError && damageAalError && revenueAalError && biDaysError)
  const hasOverviewData = adjustedScoreValue != null || totalLossDisplay.value != null || biDaysTotal != null

  if (annualLoading && !scoreData && !damageAalData && !revenueAalData && !biDaysData) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" minHeight={160}>
        <CircularProgress size={24} />
      </Box>
    )
  }

  if (annualErrored) {
    return <Alert severity="error">Could not load flood summary data.</Alert>
  }

  return (
    <Box>
      <Box
        mb={2}
        display="flex"
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1}>
        <ClimateSectionTitle title="Overview" subtitle="Summary of flood risk for the selected period" />

        {availablePeriods.length > 1 && (
          <ToggleButtonGroup
            size="small"
            value={effectivePeriod}
            exclusive
            onChange={(_, value) => {
              if (value) setSelectedPeriod(value)
            }}>
            {availablePeriods.map(period => (
              <ToggleButton key={period} value={period}>
                {canonicalPeriodLabels[period as CanonicalPeriod]}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        )}
      </Box>

      {!hasOverviewData && !annualLoading ? (
        <Alert severity="info">No flood summary data is available for this property.</Alert>
      ) : (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <ClimateMetricCard title="Risk Rating">
                {adjustedScoreValue != null ? (
                  <FloodScoreBadge score={adjustedScoreValue} size="compact" />
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Flood risk rating is unavailable for this period.
                  </Typography>
                )}
              </ClimateMetricCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <ClimateMetricCard title={totalLossDisplay.title}>
                {totalLossDisplay.value != null ? (
                  <Typography variant="h4">{formatCurrencyValue(totalLossDisplay.value, currencySymbol, 0)}</Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Annual loss data is unavailable for this period.
                  </Typography>
                )}
              </ClimateMetricCard>
            </Grid>

            <Grid item xs={12} md={4}>
              <ClimateMetricCard title={BI_DAYS_LABEL}>
                {biDaysTotal != null ? (
                  <Typography variant="h4">{`${formatPlainNumber(biDaysTotal, 1)} days/year`}</Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Business interruption days are unavailable for this period.
                  </Typography>
                )}
              </ClimateMetricCard>
            </Grid>
          </Grid>

          <Box mt={4}>
            <ClimateSectionTitle
              title={FINANCIAL_BREAKDOWN_TITLE}
              subtitle="Damage and revenue components of annualised flood impacts"
            />
          </Box>

          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12} md={6}>
              <ClimateMetricCard title={DAMAGE_AAL_LABEL}>
                {damageAalTotal != null ? (
                  <Typography variant="h4">{formatCurrencyValue(damageAalTotal, currencySymbol, 0)}</Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Damage AAL is unavailable for this period.
                  </Typography>
                )}
              </ClimateMetricCard>
            </Grid>

            <Grid item xs={12} md={6}>
              <ClimateMetricCard title={REVENUE_AAL_LABEL}>
                {revenueAalTotal != null ? (
                  <Typography variant="h4">{formatCurrencyValue(revenueAalTotal, currencySymbol, 0)}</Typography>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Revenue AAL is unavailable for this period.
                  </Typography>
                )}
              </ClimateMetricCard>
            </Grid>
          </Grid>

          <Grid container spacing={2} mt={0.5}>
            <Grid item xs={12} md={7}>
                <Card variant="outlined">
                <CardContent>
                  <ClimateSectionTitle title="Damage Breakdown" subtitle="Structure and contents split across flood sources" />
                  <Box mt={2}>
                    {!damageBreakdownHasData ? (
                      <Typography variant="body2" color="text.secondary">
                        No damage breakdown is available for this period.
                      </Typography>
                    ) : (
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell />
                            {damageSources.map(source => (
                              <TableCell key={source} align="right">
                                {formatFloodSourceLabel(source)}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {damageTargets.map(target => (
                            <TableRow key={target}>
                              <TableCell component="th" scope="row">
                                {formatDamageTargetLabel(target)}
                              </TableCell>
                              {damageSources.map(source => {
                                const value = scaledValue(sumMetricNode(damageAalPeriodNode?.[target]?.[source]), adaptationFactor)
                                return (
                                  <TableCell key={`${target}-${source}`} align="right">
                                    {formatCurrencyValue(value, currencySymbol, 0)}
                                  </TableCell>
                                )
                              })}
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={5}>
                <Card variant="outlined">
                <CardContent>
                  <ClimateSectionTitle title="Revenue AAL by Source" subtitle="Annual interruption-related revenue loss across flood sources" />
                  <Box mt={2}>
                    {!revenueBreakdownHasData ? (
                      <Typography variant="h4">—</Typography>
                    ) : (
                      <Stack spacing={1}>
                        {revenueSources.map(source => {
                          const value = scaledValue(sumMetricNode(revenueAalPeriodNode?.[source]), adaptationFactor)
                          return (
                            <Stack key={source} direction="row" justifyContent="space-between" alignItems="baseline">
                              <Typography variant="body2" color="text.secondary">
                                {formatFloodSourceLabel(source)}
                              </Typography>
                              <Typography variant="body2">{formatCurrencyValue(value, currencySymbol, 0)}</Typography>
                            </Stack>
                          )
                        })}
                      </Stack>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}
    </Box>
  )
}
